<template>
    <div class="container edit-container">
        <div class="row justify-content-center">
            <div class="col-12 case-settings-col-lg mt-5">
                <!-- Empty Message -->
                <div v-show="links.length === 0 && !loading" class="text-center mt-4">
                    <h1 class="mb-3 h5 font-weight-bolder">
                        <span class="icon icon-glyphicons-basic-link mr-2"></span>
                        <span class="pr-1">Share Links</span>
                    </h1>
                    <p class="mb-0 text-secondary">Send your scenario to anyone, using a secure link.</p>
                    <br />
                    <button @click="openCreate()" type="button" class="btn btn-primary">
                        <!-- <span class="icon icon-glyphicons-basic-link mr-1"></span> -->
                        <span>Create a Link</span>
                    </button>
                </div>

                <!-- Non-Empty Header -->
                <div v-show="links.length > 0 && !loading">
                    <div class="d-flex mb-4">
                        <div class="flex-grow-1 align-self-center">
                            <h1 class="mb-2 h5 font-weight-bolder">
                                <span class="icon icon-glyphicons-basic-link mr-2"></span>
                                <span>Share Links</span>
                            </h1>
                            <p class="mb-0 text-secondary">Send your scenario to anyone, using a secure link.</p>
                        </div>
                        <div class="flex-shrink-0 align-self-end">
                            <button
                                @click="openCreate()"
                                :disabled="caseIsReadOnly"
                                type="button"
                                class="btn btn-sm btn-primary ml-5"
                            >
                                Create a Link
                            </button>
                        </div>
                    </div>
                </div>

                <!-- List Display -->
                <ul class="list-group">
                    <li v-for="link in links" :key="link.id" class="list-group-item">
                        <div class="d-flex">
                            <div class="flex-grow-1 align-self-center pr-3">
                                <p class="mb-1 medium">{{ link.name }}</p>
                                <p class="text-secondary small mb-1">Created on {{ formatDate(link.created) }}</p>
                                <div class="small">
                                    <span class="badge badge-primary mr-1">{{ link.case_session_count }} Views</span>
                                    <span class="badge badge-gray mr-1" v-show="link.allow_comments"
                                        >{{ link.comments_count }} Comments</span
                                    >
                                    <span class="badge badge-info mr-1" v-show="link.is_publish_link"
                                        >For Learners</span
                                    >
                                    <span class="badge badge-info mr-1" v-show="link.is_active">Enabled</span>
                                    <span class="badge badge-warning mr-1" v-show="!link.is_active">Disabled</span>
                                    <span class="text-secondary ml-2" v-show="link.require_passcode"
                                        ><span
                                            class="icon icon-100 icon-glyphicons-basic-lock mr-1 align-middle"
                                        ></span>
                                        <span class="mr-1 align-middle">Passcode:</span>
                                        <span class="font-weight-500 align-middle">{{ link.passcode }}</span></span
                                    >
                                </div>
                            </div>
                            <div class="align-self-center flex-shrink-0">
                                <button
                                    @click="openDelete(link)"
                                    :disabled="caseIsReadOnly"
                                    type="button"
                                    class="btn btn-light btn-sm mr-1"
                                >
                                    <span class="icon icon-glyphicons-basic-bin"></span>
                                </button>
                                <button
                                    @click="openComments(link)"
                                    v-show="link.allow_comments"
                                    type="button"
                                    class="btn btn-light mr-1 btn-sm"
                                >
                                    <span class="icon icon-glyphicons-basic-chat-conversation icon-mr"></span>
                                    <span>Comments</span>
                                </button>
                                <router-link
                                    v-show="link.learner_reporting_enabled"
                                    :to="{ name: 'LinkReports', params: { linkId: link.id } }"
                                    class="btn btn-light btn-sm mr-1"
                                >
                                    <span class="icon icon-glyphicons-basic-bars mr-1"></span>
                                    <span>Reports</span>
                                </router-link>
                                <button
                                    @click="editLink(link)"
                                    :disabled="caseIsReadOnly"
                                    type="button"
                                    class="btn btn-light btn-sm mr-1"
                                >
                                    <span class="icon icon-glyphicons-basic-pencil"></span>
                                </button>
                                <button
                                    @click="showLink(link)"
                                    :disabled="caseIsReadOnly"
                                    type="button"
                                    class="btn btn-light btn-sm mr-1"
                                >
                                    <span class="icon icon-glyphicons-basic-link"></span>
                                    <span class="ml-1">Copy Link</span>
                                </button>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Create Modal -->
        <base-modal id="create-link-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-glyphicons-basic-link mr-1"></span>
                <span>New Link</span>
            </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <base-form
                            ref="createLinkForm"
                            class="mt-1"
                            v-model="selectedItem"
                            :fields="shareLinkFields"
                        ></base-form>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="create()" class="btn btn-primary" type="button">Create</button>
            </template>
        </base-modal>

        <!-- Edit Link Modal -->
        <base-modal id="edit-link-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-glyphicons-basic-link mr-2"></span>
                <span>Edit Link</span>
            </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <base-form
                            ref="editLinkForm"
                            v-model="selectedItem"
                            :fields="shareLinkFields"
                            :condensed-style="true"
                        >
                            <template slot="passcode-footer">
                                <div class="col-12">
                                    <button class="btn btn-tiny btn-link mt-2" @click="refreshPasscode()">
                                        <span class="icon icon-100 icon-glyphicons-basic-refresh mr-1"></span>
                                        <span>Regenerate</span>
                                    </button>
                                </div>
                            </template>
                        </base-form>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="update()" class="btn btn-primary" type="button">Save</button>
            </template>
        </base-modal>

        <!-- View Link Modal -->
        <base-modal id="view-link-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-glyphicons-basic-link mr-2"></span>
                <span>Copy Link</span>
            </template>
            <template>
                <div class="row mb-2">
                    <div class="col-12 d-flex align-items-center mt-1">
                        <ul class="nav">
                            <info-popover content="Copy as a link" placement="top" class="pointer">
                                <li
                                    class="nav-item tab-nav tab-nav-highlight mr-2"
                                    @click="setLinkType('link')"
                                    role="button"
                                >
                                    <div
                                        class="nav-link tab-nav-link d-flex flex-column justify-content-center align-items-center py-2"
                                        :class="{ active: linkType == 'link' }"
                                    >
                                        <div class="icon icon-xl icon-glyphicons-basic-link align-middle"></div>
                                        <div class="tiny text-secondary pt-1">Copy as a Link</div>
                                    </div>
                                </li>
                            </info-popover>
                            <info-popover content="Copy as an embed code" placement="top" class="pointer">
                                <li
                                    class="nav-item tab-nav tab-nav-highlight mr-1"
                                    @click="setLinkType('embed')"
                                    role="button"
                                >
                                    <div
                                        class="nav-link tab-nav-link d-flex flex-column justify-content-center align-items-center py-2"
                                        :class="{ active: linkType == 'embed' }"
                                    >
                                        <div class="icon icon-xl icon-glyphicons-basic-code align-middle"></div>
                                        <div class="tiny text-secondary pt-1">Copy Embed Code</div>
                                    </div>
                                </li>
                            </info-popover>
                        </ul>
                    </div>
                    <div class="col-12">
                        <div v-if="allowShareLinkCopy">
                            <div class="my-4">
                                <copy-link-input ref="copyLinkInput" v-model="currentShareLink" />
                                <div class="col-12" v-show="selectedItem.require_passcode">
                                    <span class="text-secondary small"
                                        ><span
                                            class="icon icon-100 icon-glyphicons-basic-lock mr-1 align-middle"
                                        ></span>
                                        <span class="mr-1 align-middle">Passcode:</span>
                                        <span class="font-weight-500 align-middle">{{
                                            selectedItem.passcode
                                        }}</span></span
                                    >
                                </div>
                            </div>
                            <div class="text-secondary small">
                                <p class="mb-0" v-show="linkType == 'link'">
                                    Anyone with this link will be able to view your scenario.
                                </p>
                                <p class="mb-0" v-show="linkType == 'embed'">
                                    Anyone with this embed code will be able to view your scenario.
                                </p>
                            </div>
                        </div>
                        <div v-else>
                            <div class="text-secondary px-md-4 mt-5 mb-4 d-flex flex-column align-items-center medium">
                                <div class="mb-2">
                                    <span class="badge badge-light ml-1 mr-1 align-middle">
                                        <span class="icon-glyphicons-basic-lock"></span>
                                        <span>Paid Only</span>
                                    </span>
                                </div>
                                <span class="align-middle">This feature is only available on paid plans.</span>
                                <upgrade-plan-prompt></upgrade-plan-prompt>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </base-modal>

        <!-- Remove Modal -->
        <base-modal id="remove-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Delete Link </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <p class="mb-0">
                            Remove link "{{ selectedItem.name }}"? This link will no longer provide access to this
                            scenario.
                        </p>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="remove()" class="btn btn-danger" type="button">Remove</button>
            </template>
        </base-modal>

        <!-- Comments Modal -->
        <base-modal id="comments-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="align-middle icon icon-glyphicons-basic-chat-conversation mr-2"></span>
                <span class="align-middle">Comments</span>
            </template>
            <template>
                <p v-if="commentScreens.length == 0 && !modalLoading" class="text-secondary text-center mt-3">
                    No comments have been submitted on this link yet.
                </p>
                <!-- Screen Filter -->
                <div v-else>
                    <div class="d-flex align-items-center mb-3 pb-2">
                        <div class="mr-2">Showing:</div>
                        <div class="form-inline">
                            <select v-model="screenDisplay" class="form-control">
                                <option value="unresolved">Unresolved</option>
                                <option value="all">All</option>
                            </select>
                        </div>
                    </div>
                </div>
                <p
                    v-show="screenDisplay == 'unresolved' && displayedScreens.length == 0"
                    class="text-secondary text-center my-4"
                >
                    There aren't any screens with unresolved comments to display.
                </p>
                <div class="my-2" v-for="(screen, index) in displayedScreens" :key="screen.id">
                    <div>
                        <!-- Screen Header -->
                        <div class="mb-2">
                            <router-link
                                :to="caseUrlPrefix + '/nodes/' + screen.id + '/'"
                                data-dismiss="modal"
                                class="mr-1 align-middle"
                            >
                                {{ screen.label }}
                            </router-link>
                            <button
                                v-show="unresolvedCommentCount(screen) === 0"
                                @click="toggleResolvedCommentsDisplay(screen)"
                                class="btn btn-link btn-sm text-secondary align-middle py-0"
                            >
                                <span v-show="!screen.showResolved">Show</span>
                                <span v-show="screen.showResolved">Hide</span>
                                {{ resolvedCommentCount(screen) }}
                                resolved comments
                            </button>
                        </div>
                        <!-- Comment List -->
                        <node-comments
                            v-show="unresolvedCommentCount(screen) > 0 || screen.showResolved"
                            :comments="screen.comments"
                            :edit-mode="false"
                            :show-admin-tools="true"
                            class="mt-3"
                        ></node-comments>
                        <!-- All-Resolved Message -->
                        <div
                            v-show="unresolvedCommentCount(screen) === 0 && !screen.showResolved"
                            class="text-secondary pt-1 pb-2"
                        >
                            <span
                                class="icon icon-glyphicons-basic-circle-empty-check text-success animate-success rounded"
                            ></span>
                            All comments resolved
                        </div>
                        <hr v-if="index < displayedScreens.length - 1" />
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-gray" type="button" data-dismiss="modal">Done</button>
            </template>
        </base-modal>
    </div>
</template>

<script>
import http from "@/axios.js"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"
import Utilities from "@/assets/js/utilities.js"
import CopyLinkInput from "@/components/CopyLinkInput"
import NodeComments from "@/components/NodeComments"
import InfoPopover from "@/components/InfoPopover"
import UpgradePlanPrompt from "@/components/UpgradePlanPrompt"

export default {
    name: "ShareLinks",
    components: {
        BaseModal,
        BaseForm,
        CopyLinkInput,
        NodeComments,
        InfoPopover,
        UpgradePlanPrompt,
    },
    data() {
        return {
            links: [],
            newLinkName: "",
            currentLink: "",
            modalLoading: false,
            selectedItem: {
                name: "",
                allow_comments: false,
                passcode: null,
                identification_method: "none",
                require_passcode: false,
                learner_reporting_enabled: false,
            },
            commentScreens: [],
            screenDisplay: "all",
            linkType: "link",
        }
    },
    computed: {
        caseIsReadOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        caseID() {
            return this.$route.params.id
        },
        freeTierActive() {
            return this.$store.getters.organizationDetails.free_tier_active
        },
        legacyUserMgmtActive() {
            return this.$store.getters.organizationDetails.user_management_mode === "legacy"
        },
        currentOrganizationId() {
            return this.$store.getters.currentOrganization.id
        },
        loading() {
            return this.$store.getters.loading
        },
        origin() {
            return window.location.origin
        },
        caseUrlPrefix() {
            return this.$store.getters.caseBaseUrl
        },
        currentShareLink() {
            if (this.linkType == "embed") {
                return this.embedCode
            }
            return this.linkURL
        },
        linkURL() {
            return this.origin + this.currentLink
        },
        allowShareLinkCopy() {
            // Don't allow coping embed codes on free tier.
            return this.linkType === "link" || !this.freeTierActive
        },
        embedCode() {
            return `<iframe src="${this.linkURL}" width="100%" height="900" style="max-width: 100%; max-height: 100%; border-radius: 8px" frameborder="0" frameborder="0" allow="microphone" allowfullscreen></iframe>`
        },
        learnerReportsDisabledMsg() {
            if (this.freeTierActive) {
                return "Paid Only"
            } else if (this.legacyUserMgmtActive) {
                return "Plan Upgrade Required"
            } else {
                return ""
            }
        },
        shareLinkFields() {
            let vm = this
            return [
                {
                    name: "Label",
                    placeholder: "Enter a label to identify this link",
                    key: "name",
                    type: "text",
                    required: true,
                    displayInline: true,
                },
                {
                    type: "divider",
                    name: "Security",
                },
                {
                    name: "Require a passcode",
                    key: "require_passcode",
                    type: "switch",
                    condensedStyle: true,
                },
                {
                    name: "Passcode",
                    hideName: true,
                    key: "passcode",
                    type: "text",
                    placeholder: "Enter a 5 digit passcode",
                    maxlength: 5,
                    hidden(value) {
                        return !value.require_passcode
                    },
                    required(value) {
                        return value.require_passcode
                    },
                },
                {
                    type: "divider",
                    name: "Learner Reporting",
                },
                {
                    name: "Enable Learner Reporting",
                    key: "learner_reporting_enabled",
                    subtext: "Capture learner results and performance data.",
                    type: "switch",
                    disabled() {
                        return vm.freeTierActive || vm.legacyUserMgmtActive
                    },
                    badge: this.learnerReportsDisabledMsg,
                },
                {
                    name: "",
                    key: "identification_method",
                    type: "radio",
                    isSubOption: true,
                    options: [
                        {
                            label: "Anonymous",
                            subtext: "Learner analytics will be collected anonymously.",
                            value: "none",
                        },
                        {
                            label: "Email",
                            value: "email",
                            subtext: "Ask learners to enter their emails.",
                        },
                        {
                            label: "Student or Participant Number",
                            value: "student_number",
                            subtext: "Ask learners to enter their student or participant number.",
                        },
                    ],
                    hidden(value) {
                        return !value.learner_reporting_enabled
                    },
                    condensedStyle: true,
                },
                {
                    type: "divider",
                    name: "Reviewer Tools",
                },
                {
                    name: "Enable Comments",
                    key: "allow_comments",
                    subtext: "Allow viewers to leave comments on screens.",
                    type: "switch",
                    badge: vm.freeTierActive ? "Paid Only" : false,
                    disabled() {
                        return vm.freeTierActive
                    },
                },
                {
                    name: "Enable Flexible Navigation",
                    key: "enable_node_navigation",
                    subtext: "When enabled, reviewers will be able to freely navigate to any screen and skip videos.",
                    type: "switch",
                    badge: vm.freeTierActive ? "Paid Only" : "",
                    badgeClass: vm.freeTierActive ? "badge-gray" : "",
                    disabled() {
                        return vm.freeTierActive
                    },
                },
                {
                    type: "divider",
                    hidden(value) {
                        return !value.id
                    },
                },
                {
                    name: "Link Enabled",
                    type: "switch",
                    key: "is_active",
                    subtext: "Turn this setting off to temporarily disable all usage of this link.",
                    hidden(value) {
                        return !value.id
                    },
                },
            ]
        },
        displayedScreens() {
            if (this.screenDisplay === "all") {
                return this.commentScreens
            } else {
                return this.commentScreens.filter((screen) => {
                    // Include screens that have any unresolved or recently resolved comments.
                    return screen.comments.filter((c) => !c.is_resolved || c.recentlyResolved).length > 0
                })
            }
        },
    },
    methods: {
        formatDate(date) {
            return Utilities.formatDateTime(date)
        },
        fetchData() {
            this.$store.dispatch("addLoad")
            let vm = this
            http.get("/api/cases/" + this.caseID + "/links/").then(function (response) {
                vm.links = response.data
                vm.$store.dispatch("removeLoad")
            })
        },
        openCreate() {
            this.$refs.createLinkForm.resetValidate()
            this.selectedItem = {
                name: "",
                allow_comments: false,
                passcode: null,
                identification_method: "none",
                learner_reporting_enabled: !this.freeTierActive,
            }
            $("#create-link-modal").modal("show")
            let vm = this
            http.get("/api/links/generate-passcode/").then(function (response) {
                vm.selectedItem.passcode = response.data.passcode
            })
        },
        create() {
            if (!this.$refs.createLinkForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.post("/api/cases/" + this.caseID + "/links/", this.selectedItem)
                .then(function () {
                    vm.newLinkName = ""
                    $("#create-link-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Link created.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        update() {
            if (!this.$refs.editLinkForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.put("/api/links/" + this.selectedItem.id + "/", this.selectedItem)
                .then(function () {
                    vm.fetchData()
                    $("#edit-link-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Link upated.",
                        data: {},
                    })
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        showLink(link) {
            this.selectedItem = Object.assign({}, link)
            this.linkType = "link"
            this.currentLink = link.access_url
            this.$refs.copyLinkInput.reset()
            $("#view-link-modal").modal("show")
        },
        editLink(link) {
            this.selectedItem = Object.assign({}, link)
            this.currentLink = link.access_url
            $("#edit-link-modal").modal("show")
        },
        openDelete(link) {
            this.selectedItem = link
            $("#remove-modal").modal("show")
        },
        remove() {
            this.modalLoading = true
            let vm = this
            http.put("/api/links/" + this.selectedItem.id + "/remove/")
                .then(function () {
                    vm.fetchData()
                    $("#remove-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Link removed.",
                        data: {},
                    })
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        openComments(link) {
            this.commentScreens = []
            this.selectedItem = link
            this.modalLoading = true
            this.screenDisplay = "all"
            $("#comments-modal").modal("show")
            let vm = this
            http.get("/api/links/" + link.id + "/comments/")
                .then(function (response) {
                    vm.commentScreens = response.data
                    vm.$nextTick(() => {
                        $('[data-toggle="popover"]').popover()
                    })
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        resolvedCommentCount(screen) {
            return screen.comments.filter((c) => c.is_resolved === true).length
        },
        unresolvedCommentCount(screen) {
            return screen.comments.filter((c) => c.is_resolved === false).length
        },
        toggleResolvedCommentsDisplay(screen) {
            if (screen.showResolved === undefined) {
                this.$set(screen, "showResolved", true)
            } else if (screen.showResolved) {
                screen.showResolved = false
            } else {
                screen.showResolved = true
            }
        },
        setLinkType(linkType) {
            this.linkType = linkType
            this.$refs.copyLinkInput.reset()
        },
        refreshPasscode() {
            this.selectedItem.passcode = Utilities.getRandomFiveDigitStr()
        },
    },
    created() {
        this.fetchData()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/hotspots.scss";
@import "@/assets/styles/variables.scss";

.quote-border {
    border-left-width: 2px !important;
    padding-left: 12px;
}

.animate-success {
    animation: pulse-sm-active-success 0.6s linear;
}
</style>